import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Avatar,
  useTheme,
  Divider,
  Button,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Drawer,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  PushPinOutlined as PinIcon,
  ContentCopy as ContentCopyIcon,
  Input as InsertIcon,
  DescriptionOutlined as ContextIcon,
  ThumbUpAltOutlined as ThumbUpIcon,
  ThumbDownAltOutlined as ThumbDownIcon,
  ThumbUpAlt as ThumbUpFilledIcon,
  ThumbDownAlt as ThumbDownFilledIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';
import discoveryIconWhite from '../../assets/icons/discovery_icon_white.png';
import discoveryIconBlack from '../../assets/icons/discovery_icon_black.png';
import http from '../../services/http';
import OrbyImage from './OrbyImage';
import { adjustTextWithGpt } from '../../utils/aiTextTools';
import { BLOQUEADO, DESMARCAR, acaoDoFeedback, dicaDoPolegar } from '../../utils/acaoDoFeedbackDoOrby';
import { toast } from 'react-toastify';

// Persistência do conversation_id ativo do Orby: como toda navegação de página
// remonta o Template (rotas usam componente inline em App.js) e o AIAssistant junto,
// o estado do componente (useState) morre a cada troca de página/aba. Guardamos só o
// ID da conversa (nunca o conteúdo das mensagens — pode ter dado de cliente e
// localStorage não é lugar pra isso) e, ao montar, recarregamos a conversa da API
// pelo mesmo caminho já usado ao selecionar uma conversa no histórico.
const ORBY_CONVERSATION_STORAGE_KEY = 'orbyConversationId';

const getStoredOrbyConversationId = () => {
  try {
    return localStorage.getItem(ORBY_CONVERSATION_STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
};

const persistOrbyConversationId = (id) => {
  try {
    if (id) {
      localStorage.setItem(ORBY_CONVERSATION_STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(ORBY_CONVERSATION_STORAGE_KEY);
    }
  } catch (e) {
    // ignore (modo privado / storage desabilitado)
  }
};

const AIAssistant = ({ open, onClose, fullScreen = false, variant = 'dialog' }) => {
  const theme = useTheme();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  // Contexto do ticket/chat aberto anexado à próxima pergunta ({ label, text }).
  const [attachedContext, setAttachedContext] = useState(null);
  // Feedback (like/dislike) por CONTEÚDO da resposta — persistente: é recarregado do
  // backend ao abrir a conversa, então a escolha do usuário fica marcada pra sempre.
  const [feedbackByResponse, setFeedbackByResponse] = useState({});
  // `id` e `status` de cada feedback, para poder DESMARCAR (ponto 17).
  // Mapa separado em vez de engrossar `feedbackByResponse`: aquele e lido como
  // string ('like'/'dislike') em varios pontos da tela, e trocar a forma dele
  // mexeria em tudo o que o le.
  const [feedbackMetaByResponse, setFeedbackMetaByResponse] = useState({});
  // Documentos (RAG) usados em cada resposta — enviados no feedback para a Fase 2
  // (ajuste de ranqueamento por documento).
  const [sourcesByResponse, setSourcesByResponse] = useState({});
  const [dislikeDialog, setDislikeDialog] = useState(null);
  const [dislikeText, setDislikeText] = useState('');
  const [loading, setLoading] = useState(false);
  const [gptAdjustingInputMessage, setGptAdjustingInputMessage] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [chatConfigurations, setChatConfigurations] = useState([]);
  const [selectedConfigurationId, setSelectedConfigurationId] = useState('');
  const [historyExtraConfig, setHistoryExtraConfig] = useState(null);
  const selectionIdsFromHistoryRef = useRef(new Set());
  const chatConfigurationsRef = useRef([]);
  const [conversationTitle, setConversationTitle] = useState(null);
  const [attachedImage, setAttachedImage] = useState(null);
  const [attachedImagePreview, setAttachedImagePreview] = useState(null);
  const [imagemAmpliada, setImagemAmpliada] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const historyButtonRef = useRef(null);
  const fileInputRef = useRef(null);

  const OrbyInlineIcon = ({ loading: isLoading }) => {
    if (isLoading) {
      return (
        <CircularProgress
          size={18}
          thickness={6}
          sx={{
            color: 'primary.main',
            '@keyframes orbyPulse': {
              '0%': { opacity: 0.45 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.45 },
            },
            animation: 'orbyPulse 1s ease-in-out infinite',
          }}
        />
      );
    }
    return (
      <Box
        component="img"
        src={theme.palette.mode === 'dark' ? discoveryIconWhite : discoveryIconBlack}
        alt="Orby"
        sx={{ width: 18, height: 18, objectFit: 'contain' }}
      />
    );
  };

  const providerLabel = (p) =>
    ({ openai: 'OpenAI', claude: 'Claude', gemini: 'Gemini' }[p] || p || '');

  const orbyHeaderChrome = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    const fg = theme.palette.text.primary;
    const fgMuted = theme.palette.text.secondary;
    const iconFg = theme.palette.background.icon;
    return {
      fg,
      fgMuted,
      iconFg,
      titleShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.25)' : 'none',
      selectFieldBg: isDark ? 'rgba(255, 255, 255, 0.12)' : theme.palette.background.paper,
      selectBorder: isDark ? 'rgba(255, 255, 255, 0.3)' : theme.palette.border.primary,
      selectBorderHover: isDark ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
    };
  }, [theme]);

  const chatConfigurationsForSelect = useMemo(() => {
    const base = [...chatConfigurations];
    if (
      historyExtraConfig &&
      !base.some((c) => Number(c.id) === Number(historyExtraConfig.id))
    ) {
      base.push({
        id: historyExtraConfig.id,
        name: historyExtraConfig.name,
        provider: historyExtraConfig.provider,
        active: !!historyExtraConfig.active,
      });
    }
    return base;
  }, [chatConfigurations, historyExtraConfig]);

  useEffect(() => {
    chatConfigurationsRef.current = chatConfigurations;
  }, [chatConfigurations]);

  useEffect(() => {
    if (open) {
      loadChatConfigurations();
      loadConversations();
      if (!conversationId && messages.length === 0) {
        const storedConversationId = getStoredOrbyConversationId();
        if (storedConversationId) {
          // Restaura a conversa que estava ativa antes do remount (troca de
          // página/aba). Se não existir mais (deletada / de outro usuário), cai
          // graciosamente numa conversa nova — sem toast de erro.
          loadConversation(storedConversationId, { fallbackToWelcomeOnFailure: true });
        } else {
          setMessages([{
            role: 'assistant',
            content: 'Olá! Sou o assistente virtual da plataforma Alodesk. Como posso ajudá-lo hoje? Posso responder dúvidas sobre funcionalidades, processos e orientações sobre o uso da plataforma.',
            timestamp: new Date(),
            isWelcome: true,
          }]);
        }
      }
    }
  }, [open]);

  const loadChatConfigurations = async () => {
    try {
      const response = await http.get('/ai-configurations/for-chat');
      if (response.data.success) {
        const list = response.data.configurations || [];
        setChatConfigurations(list);
        setSelectedConfigurationId((prev) => {
          const prevNum = prev === '' || prev == null ? null : Number(prev);
          if (prevNum != null && !Number.isNaN(prevNum)) {
            if (list.some((c) => Number(c.id) === prevNum)) return prevNum;
            if (selectionIdsFromHistoryRef.current.has(prevNum)) return prevNum;
          }
          const active = list.find((c) => c.active);
          if (active) return active.id;
          return list[0]?.id ?? '';
        });
      } else {
        setChatConfigurations([]);
        setSelectedConfigurationId('');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de IA:', error);
      setChatConfigurations([]);
      setSelectedConfigurationId('');
    }
  };

  const wipeConversationToWelcome = () => {
    setConversationId(null);
    persistOrbyConversationId(null);
    setConversationTitle(null);
    setMessages([{
      role: 'assistant',
      content: 'Olá! Sou o assistente virtual da plataforma Alodesk. Como posso ajudá-lo hoje? Posso responder dúvidas sobre funcionalidades, processos e orientações sobre o uso da plataforma.',
      timestamp: new Date(),
      isWelcome: true,
    }]);
    setAttachedImage(null);
    setAttachedImagePreview(null);
  };

  const resetChatToWelcome = () => {
    selectionIdsFromHistoryRef.current.clear();
    setHistoryExtraConfig(null);
    wipeConversationToWelcome();
    setSelectedConfigurationId((prev) => {
      const prevNum = prev === '' || prev == null ? null : Number(prev);
      const list = chatConfigurationsRef.current;
      if (prevNum != null && !Number.isNaN(prevNum) && list.some((c) => Number(c.id) === prevNum)) {
        return prevNum;
      }
      const active = list.find((c) => c.active);
      if (active) return active.id;
      return list[0]?.id ?? '';
    });
  };

  const handleSelectConfiguration = (configId) => {
    if (configId === selectedConfigurationId) return;
    selectionIdsFromHistoryRef.current.clear();
    setHistoryExtraConfig(null);
    setSelectedConfigurationId(configId);
    wipeConversationToWelcome();
  };

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const response = await http.get('/ai-assistant/conversations');
      if (response.data.success) {
        setConversations(response.data.conversations || []);
      }
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (convId, options = {}) => {
    const { fallbackToWelcomeOnFailure = false } = options;
    try {
      setLoading(true);
      const response = await http.get(`/ai-assistant/conversations/${convId}`);
      if (response.data.success) {
        const conv = response.data.conversation;
        setConversationId(conv.conversation_id);
        persistOrbyConversationId(conv.conversation_id);
        setConversationTitle(conv.title || null);
        const cfgId =
          conv.ai_configuration_id != null && conv.ai_configuration_id !== ''
            ? Number(conv.ai_configuration_id)
            : null;
        if (cfgId != null && !Number.isNaN(cfgId)) {
          selectionIdsFromHistoryRef.current.add(cfgId);
          setSelectedConfigurationId(cfgId);
        }
        if (conv.ai_configuration) {
          setHistoryExtraConfig(conv.ai_configuration);
        } else if (cfgId != null && !Number.isNaN(cfgId)) {
          setHistoryExtraConfig({
            id: cfgId,
            name: `Configuração #${cfgId}`,
            provider: '',
            active: false,
          });
        } else {
          setHistoryExtraConfig(null);
        }
        const loadedMessages = (conv.messages || []).map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
          // A imagem anexada passou a ser gravada em disco, e o caminho vem
          // na mensagem. Sem trazer isto para ca, ela apareceria so na aba
          // aberta e sumiria ao reabrir a conversa pelo historico.
          imagePath: msg.image_path || null,
        }));
        setMessages(loadedMessages);

        // Fase 2: remonta o mapa de fontes do RAG que a API agora persiste em
        // used_document_ids. Sem isto, o like/dislike numa conversa restaurada do
        // historico entrava sem os documentos que a embasaram e nao alimentava o
        // re-ranqueamento por feedback. Indexado pelo conteudo da resposta, igual
        // ao fluxo ao vivo. Conversas antigas nao tem o campo: ficam como antes.
        const restoredSources = {};
        (conv.messages || []).forEach(msg => {
          if (msg.role !== 'assistant' || !Array.isArray(msg.used_document_ids)) return;
          const ids = msg.used_document_ids.filter(v => v != null);
          if (ids.length) restoredSources[msg.content] = ids;
        });
        setSourcesByResponse(prev => ({ ...prev, ...restoredSources }));
        setHistoryOpen(false);
      } else if (fallbackToWelcomeOnFailure) {
        // Resposta 200 mas success:false (não deveria acontecer, mas por segurança).
        wipeConversationToWelcome();
      }
    } catch (error) {
      console.error('Erro ao carregar conversa:', error);
      if (fallbackToWelcomeOnFailure) {
        // Conversa guardada localmente não existe mais (deletada ou de outro
        // usuário) — cai numa conversa nova, sem crashar e sem toast de erro.
        wipeConversationToWelcome();
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja deletar esta conversa?')) {
      return;
    }
    try {
      const response = await http.delete(`/ai-assistant/conversations/${convId}`);
      if (response.data.success) {
        setConversations(prev => prev.filter(c => c.conversation_id !== convId));
        // Se a conversa deletada era a atual, limpar
        if (conversationId === convId) {
          handleClearChat();
        }
      }
    } catch (error) {
      console.error('Erro ao deletar conversa:', error);
    }
  };

  // Scroll automático para a última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focar no input quando o modal abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInputMessage('');
      setConversationId(null);
      setConversationTitle(null);
      setAttachedImage(null);
      setAttachedImagePreview(null);
      selectionIdsFromHistoryRef.current.clear();
      setHistoryExtraConfig(null);
    }
  }, [open]);

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });


  /**
   * Como o Orby desenha um link.
   *
   * Sem isto, o `ReactMarkdown` gera um `<a>` puro e o clique SUBSTITUI a aba
   * atual -- levando embora a conversa, os rascunhos abertos e as abas de
   * chamado do operador. Abrir noutra aba e o comportamento esperado de um
   * link citado por um assistente.
   *
   * O `rel` acompanha por seguranca: sem ele, a pagina aberta ganha acesso a
   * `window.opener` e pode redirecionar a nossa.
   */
  const componentesMarkdown = useMemo(() => ({
    a: ({ node, ...props }) => (
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      <a {...props} target="_blank" rel="noopener noreferrer" />
    ),
  }), []);
  const handleSendMessage = async () => {
    const hasText = inputMessage.trim();
    const hasImage = !!attachedImage;
    if ((!hasText && !hasImage) || loading) return;

    const userMessage = hasText ? inputMessage.trim() : 'O que você vê nesta imagem? Pode descrever ou tirar dúvidas sobre ela.';
    setInputMessage('');
    // Consome o contexto anexado (vai só no payload da IA, não no balão exibido).
    const ctx = attachedContext;
    if (ctx) setAttachedContext(null);
    
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      hasImage: hasImage,
      // A previa local faz a imagem aparecer no mesmo instante do envio,
      // sem esperar o caminho voltar do servidor.
      imagePreview: attachedImagePreview || null,
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    let imageDataUrl = null;
    if (attachedImage) {
      try {
        imageDataUrl = await readFileAsDataUrl(attachedImage);
      } catch (e) {
        console.error('Erro ao ler imagem:', e);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Não foi possível enviar a imagem. Tente outro arquivo.',
          timestamp: new Date(),
          error: true,
        }]);
        setLoading(false);
        return;
      }
      setAttachedImage(null);
      setAttachedImagePreview(null);
    }

    try {
      const history = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const payload = {
        message: ctx
          ? `Contexto (${ctx.label}):\n${ctx.text}\n\n---\nCom base no contexto acima, responda:\n${userMessage}`
          : userMessage,
        conversation_id: conversationId,
        history: history,
      };
      if (selectedConfigurationId !== '' && selectedConfigurationId != null) {
        payload.ai_configuration_id = Number(selectedConfigurationId);
      }
      if (imageDataUrl) payload.image = imageDataUrl;

      // O cliente HTTP tem 10s de teto, curto demais para esta chamada:
      // uma pergunta com imagem envolve tokens de visao e, quando o provedor
      // tropeca, ainda ha as retentativas do lado da API. Com 10s o operador
      // via um erro de timeout enquanto a resposta ainda estava a caminho.
      const response = await http.post('/ai-assistant/chat', payload, { timeout: 120000 });

      if (response.data.success) {
        if (response.data.conversation_id) {
          setConversationId(response.data.conversation_id);
          persistOrbyConversationId(response.data.conversation_id);
          if (response.data.conversation && response.data.conversation.title) {
            setConversationTitle(response.data.conversation.title);
          }
          loadConversations();
        }

        // Adicionar resposta da IA
        const aiMessage = {
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date()
        };

        // Guarda os documentos do RAG usados nesta resposta (para o feedback / Fase 2).
        const srcIds = (response.data.rag?.sources || [])
          .map((s) => s && s.document_id)
          .filter((v) => v != null);
        if (srcIds.length) {
          setSourcesByResponse((prev) => ({ ...prev, [response.data.message]: srcIds }));
        }

        setMessages(prev => [...prev, aiMessage]);
      } else {
        const errorMessage = {
          role: 'assistant',
          content: `Desculpe, ocorreu um erro: ${response.data.error || 'Erro desconhecido'}. Por favor, tente novamente.`,
          timestamp: new Date(),
          error: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Desculpe, não consegui processar sua mensagem. Verifique sua conexão e tente novamente.',
        timestamp: new Date(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAdjustInputWithGpt = async () => {
    const current = String(inputMessage || '').trim();
    if (!current) {
      toast.warning('Digite uma mensagem antes de ajustar com Orby.');
      return;
    }
    if (loading || historyOpen || gptAdjustingInputMessage) return;
    try {
      setGptAdjustingInputMessage(true);
      const res = await adjustTextWithGpt({ text: current, kind: 'mensagem do Orby' });
      if (!res.ok) {
        toast.error(res.message || 'Não foi possível ajustar o texto.');
        return;
      }
      setInputMessage(res.text);
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (e) {
      toast.error('Erro ao ajustar texto com Orby.');
    } finally {
      setGptAdjustingInputMessage(false);
    }
  };

  const handleClearChat = () => {
    resetChatToWelcome();
  };

  const MAX_IMAGE_SIZE_MB = 5;

  const addImageFromFile = (file) => {
    if (!file?.type?.startsWith('image/')) return;
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) return;
    setAttachedImage(file);
    const reader = new FileReader();
    reader.onload = () => setAttachedImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) addImageFromFile(file);
  };

  const handlePaste = (e) => {
    const files = e.clipboardData?.files;
    if (files?.length) {
      const img = Array.from(files).find((f) => f.type.startsWith('image/'));
      if (img) {
        e.preventDefault();
        e.stopPropagation();
        addImageFromFile(img);
      }
    }
  };

  const location = useLocation();
  // O que está aberto na área principal (para oferecer "usar como contexto").
  const openContextKind = /^\/ticket\/\d+/.test(location.pathname)
    ? 'ticket'
    : (location.pathname.startsWith('/chat') ? 'chat' : null);

  // Pede ao ticket/chat aberto um resumo para usar como contexto da próxima pergunta.
  const requestOpenContext = () => {
    let done = false;
    const onResp = (e) => {
      done = true;
      const d = e?.detail || {};
      if (d.text) {
        setAttachedContext({ label: d.label || 'Contexto', text: String(d.text).slice(0, 6000) });
      } else {
        toast.info('Não há conteúdo para usar como contexto.');
      }
    };
    window.addEventListener('orbyContextResponse', onResp, { once: true });
    window.dispatchEvent(new CustomEvent('orbyRequestContext'));
    setTimeout(() => {
      window.removeEventListener('orbyContextResponse', onResp);
      if (!done) toast.warning('Não foi possível obter o contexto do que está aberto.');
    }, 400);
  };

  // Feedback (like/dislike) das respostas do Orby.
  const aiConfigIdForFeedback = () =>
    (selectedConfigurationId !== '' && selectedConfigurationId != null) ? Number(selectedConfigurationId) : null;

  const submitFeedback = async (payload) => {
    try {
      const res = await http.post('/ai-feedback', payload);
      // O id volta do `store` e e o que permite desmarcar sem recarregar a
      // conversa -- sem ele, o desmarcar so funcionaria depois de reabrir.
      return res.data?.id ?? true;
    } catch (e) {
      toast.error('Não foi possível registrar o feedback.');
      return false;
    }
  };

  /** O feedback que existe nesta resposta, na forma que `acaoDoFeedback` espera. */
  const feedbackAtual = (response) => {
    const rating = feedbackByResponse[response];
    if (!rating) return null;
    return { rating, status: feedbackMetaByResponse[response]?.status ?? null };
  };

  const esquecerFeedback = (response) => {
    setFeedbackByResponse((prev) => { const n = { ...prev }; delete n[response]; return n; });
    setFeedbackMetaByResponse((prev) => { const n = { ...prev }; delete n[response]; return n; });
  };

  /** Desmarca o feedback desta resposta. Ponto 17 da acao 20. */
  const desmarcarFeedback = async (response) => {
    const id = feedbackMetaByResponse[response]?.id;

    if (!id) {
      toast.warning('Recarregue a conversa para poder desmarcar este feedback.');
      return;
    }

    const anterior = {
      rating: feedbackByResponse[response],
      meta: feedbackMetaByResponse[response],
    };

    // Desmarca otimista, e repoe se a api recusar -- ela e a palavra final
    // sobre o revisado (409).
    esquecerFeedback(response);

    try {
      await http.delete(`/ai-feedback/${id}`);
    } catch (e) {
      setFeedbackByResponse((prev) => ({ ...prev, [response]: anterior.rating }));
      setFeedbackMetaByResponse((prev) => ({ ...prev, [response]: anterior.meta }));
      toast.error(e?.response?.data?.message || 'Não foi possível desmarcar o feedback.');
    }
  };

  const handleLike = async (question, response) => {
    const acao = acaoDoFeedback(feedbackAtual(response), 'like');

    if (acao === BLOQUEADO) {
      // Explica em vez de o clique nao fazer nada em silencio, que era o
      // comportamento antigo (`if (feedbackByResponse[response]) return`).
      toast.info(dicaDoPolegar(feedbackAtual(response), 'like'));
      return;
    }

    if (acao === DESMARCAR) {
      await desmarcarFeedback(response);
      return;
    }

    // Marca otimista (persiste: será recarregado do backend ao reabrir a conversa).
    setFeedbackByResponse((prev) => ({ ...prev, [response]: 'like' }));
    const resultado = await submitFeedback({
      rating: 'like', question, response,
      conversation_id: conversationId,
      ai_configuration_id: aiConfigIdForFeedback(),
      used_document_ids: sourcesByResponse[response] || undefined,
    });
    if (resultado) {
      setFeedbackMetaByResponse((prev) => ({
        ...prev,
        [response]: { id: typeof resultado === 'number' ? resultado : null, status: 'pending' },
      }));
      toast.success('Obrigado pelo feedback!');
    } else {
      esquecerFeedback(response);
    }
  };

  const openDislike = async (question, response) => {
    const acao = acaoDoFeedback(feedbackAtual(response), 'dislike');

    if (acao === BLOQUEADO) {
      toast.info(dicaDoPolegar(feedbackAtual(response), 'dislike'));
      return;
    }

    if (acao === DESMARCAR) {
      await desmarcarFeedback(response);
      return;
    }

    setDislikeText('');
    setDislikeDialog({ question, response });
  };

  const submitDislike = async () => {
    if (!dislikeDialog) return;
    const text = dislikeText.trim();
    if (!text) {
      toast.warning('Descreva o que ficou errado para enviar.');
      return;
    }
    const response = dislikeDialog.response;
    setFeedbackByResponse((prev) => ({ ...prev, [response]: 'dislike' }));
    setDislikeDialog(null);
    const ok = await submitFeedback({
      rating: 'dislike', justification: text,
      question: dislikeDialog.question, response,
      conversation_id: conversationId,
      ai_configuration_id: aiConfigIdForFeedback(),
      used_document_ids: sourcesByResponse[response] || undefined,
    });
    if (ok) {
      setFeedbackMetaByResponse((prev) => ({
        ...prev,
        [response]: { id: typeof ok === 'number' ? ok : null, status: 'pending' },
      }));
      toast.success('Feedback enviado para análise. Obrigado!');
    } else {
      esquecerFeedback(response);
    }
  };

  // Persistência: ao abrir/trocar de conversa, recarrega os likes/dislikes que o usuário
  // já deu (marca de volta nas respostas). Sem conversationId ainda, começa limpo.
  useEffect(() => {
    if (!conversationId) {
      setFeedbackByResponse({});
      setFeedbackMetaByResponse({});
      return undefined;
    }
    let cancelled = false;
    http.get('/ai-feedback/mine', { params: { conversation_id: conversationId } })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        const meta = {};
        (res.data || []).forEach((f) => {
          if (!f || !f.response) return;
          map[f.response] = f.rating;
          // `id` e `status` chegam para o desmarcar: sem o id nao ha o que
          // apagar, e sem o status a tela ofereceria desmarcar um feedback que
          // a equipe ja revisou, so para a api recusar com 409.
          meta[f.response] = { id: f.id ?? null, status: f.status ?? null };
        });
        setFeedbackByResponse(map);
        setFeedbackMetaByResponse(meta);
      })
      .catch(() => { /* silencioso */ });
    return () => { cancelled = true; };
  }, [conversationId]);

  // Copia a resposta do Orby para a área de transferência.
  const copyOrbyMessage = (text) => {
    try {
      navigator.clipboard.writeText(text || '');
      toast.success('Resposta copiada.');
    } catch (e) {
      toast.error('Não foi possível copiar.');
    }
  };

  // Insere a resposta no ticket/chat ABERTO (a caixa de ação do ticket ou o campo de
  // mensagem do chat escutam o evento e confirmam com um ack). Sem nada aberto, copia.
  const insertOrbyMessage = (text) => {
    let handled = false;
    const onAck = () => { handled = true; };
    window.addEventListener('orbyInsertTextAck', onAck, { once: true });
    window.dispatchEvent(new CustomEvent('orbyInsertText', { detail: { text: text || '' } }));
    setTimeout(() => {
      window.removeEventListener('orbyInsertTextAck', onAck);
      if (handled) {
        toast.success('Inserido no ticket/chat aberto.');
      } else {
        try {
          navigator.clipboard.writeText(text || '');
          toast.info('Nenhum ticket/chat aberto — resposta copiada.');
        } catch (e) { /* ignore */ }
      }
    }, 180);
  };

  // Fixa o Orby como uma aba no acesso rápido (persistente). O QuickAccess escuta o
  // evento e passa a exibir a aba, que abre/fecha o painel lateral.
  const pinToQuickAccess = () => {
    try {
      localStorage.setItem('quickAccessOrby', JSON.stringify([{ id: 'orby', name: 'Orby', key: 'orby' }]));
      window.dispatchEvent(new CustomEvent('quickAccessOrbyChanged'));
      toast.success('Orby fixado no acesso rápido.');
    } catch (e) {
      // ignore
    }
  };

  const body = (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          background: theme.palette.background.chatHeader,
          color: theme.palette.text.primary,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Box
            component="img"
            src={theme.palette.mode === 'dark' ? discoveryIconWhite : discoveryIconBlack}
            alt="Orby"
            sx={{ width: 28, height: 28, flexShrink: 0, objectFit: 'contain' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              component="div"
              sx={{
                color: orbyHeaderChrome.fg,
                fontWeight: 600,
                textShadow: orbyHeaderChrome.titleShadow,
                flexShrink: 0,
              }}
            >
              Orby
            </Typography>
            {conversationTitle && (
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: orbyHeaderChrome.fgMuted,
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  ml: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px',
                  '&::before': {
                    content: '"•"',
                    mr: 0.5,
                    opacity: 0.7,
                  },
                }}
              >
                {conversationTitle}
              </Typography>
            )}
          </Box>
          {chatConfigurationsForSelect.length > 0 && (
            <FormControl
              size="small"
              sx={{
                minWidth: 140,
                maxWidth: 220,
                ml: 1,
                flexShrink: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: orbyHeaderChrome.selectFieldBg,
                  color: orbyHeaderChrome.fg,
                  fontSize: '0.75rem',
                  height: '32px',
                  '& fieldset': {
                    borderColor: orbyHeaderChrome.selectBorder,
                  },
                  '&:hover fieldset': {
                    borderColor: orbyHeaderChrome.selectBorderHover,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: orbyHeaderChrome.fgMuted,
                  fontSize: '0.75rem',
                  transform: 'translate(14px, 9px) scale(1)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                },
                '& .MuiSelect-select': {
                  padding: '6px 32px 6px 14px',
                  fontSize: '0.75rem',
                },
                '& .MuiSvgIcon-root': {
                  color: orbyHeaderChrome.iconFg,
                  fontSize: '1.2rem',
                },
              }}
            >
              <InputLabel id="orby-ia-label">IA</InputLabel>
              <Select
                labelId="orby-ia-label"
                value={selectedConfigurationId === '' ? '' : String(selectedConfigurationId)}
                onChange={(e) => handleSelectConfiguration(Number(e.target.value))}
                label="IA"
              >
                {chatConfigurationsForSelect.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)} sx={{ fontSize: '0.8rem' }}>
                    {c.name}
                    {c.active ? ' (ativa)' : ' (fora do Orby)'}
                    {c.provider ? ` · ${providerLabel(c.provider)}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!fullScreen && (
            <Tooltip title="Fixar o Orby no acesso rápido" arrow>
              <IconButton
                onClick={pinToQuickAccess}
                sx={{ color: orbyHeaderChrome.iconFg }}
                size="small"
              >
                <PinIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            ref={historyButtonRef}
            onClick={() => setHistoryOpen(true)}
            sx={{ color: orbyHeaderChrome.iconFg }}
            size="small"
            title="Histórico de conversas"
          >
            <HistoryIcon />
          </IconButton>
          {!fullScreen && (
            <IconButton
              onClick={onClose}
              sx={{ color: orbyHeaderChrome.iconFg }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
        }}
      >
        {/* Área de mensagens */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
          }}
        >
          {messages.map((message, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 1,
                mb: 1,
              }}
            >
              {message.role === 'assistant' && (
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: 32,
                    height: 32,
                  }}
                >
                  <Box
                    component="img"
                    src={theme.palette.mode === 'dark' ? discoveryIconWhite : discoveryIconBlack}
                    alt=""
                    sx={{ width: 20, height: 20, objectFit: 'contain' }}
                  />
                </Avatar>
              )}
              
              <Paper
                elevation={message.role === 'user' ? 2 : 1}
                sx={{
                  p: 1.5,
                  maxWidth: '75%',
                  backgroundColor: message.role === 'user' 
                    ? theme.palette.primary.main 
                    : message.error
                    ? theme.palette.error.light
                    : theme.palette.mode === 'dark' 
                    ? '#2d2d2d' 
                    : '#ffffff',
                  color: message.role === 'user'
                    // Balão do usuário tem fundo primary.main; o texto precisa
                    // contrastar com ELE (contrastText) e não com o fundo da tela —
                    // no tema claro, text.primary era escuro e sumia (item 19).
                    ? theme.palette.primary.contrastText
                    : '#212121',
                  borderRadius: 2,
                  border: message.role === 'assistant' && theme.palette.mode === 'light' 
                    ? '1px solid #e0e0e0' 
                    : 'none',
                }}
              >
                {message.role === 'assistant' ? (
                  <Box
                    sx={{
                      '& p': { margin: '0 0 0.2em 0' },
                      '& p:last-child': { marginBottom: 0 },
                      '& strong': { fontWeight: 600 },
                      '& em': { fontStyle: 'italic' },
                      '& code': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                        padding: '1px 3px',
                        borderRadius: 1,
                        fontSize: '0.9em',
                        fontFamily: 'monospace',
                      },
                      '& pre': {
                        overflow: 'auto',
                        margin: '0.2em 0',
                        padding: 6,
                        borderRadius: 1,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)',
                      },
                      '& pre code': { backgroundColor: 'transparent', padding: 0 },
                      '& ul, & ol': { margin: '0.05em 0', paddingLeft: 8 },
                      '& li': { marginBottom: '0.05em' },
                      '& h1, & h2, & h3': { margin: '0.25em 0 0.15em 0', fontWeight: 600 },
                      '& h1': { fontSize: '1.15em' },
                      '& h2': { fontSize: '1.05em' },
                      '& h3': { fontSize: '0.95em' },
                      '& a': { color: theme.palette.primary.main, textDecoration: 'none' },
                      '& a:hover': { textDecoration: 'underline' },
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: theme.palette.text.primary,
                      lineHeight: 1.4,
                    }}
                  >
                    <ReactMarkdown components={componentesMarkdown}>{message.content}</ReactMarkdown>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      // Este Typography é só do balão do usuário (fundo primary.main):
                      // usa contrastText p/ ficar legível no tema claro (item 19).
                      color: theme.palette.primary.contrastText,
                      lineHeight: 1.6,
                      fontWeight: 500,
                    }}
                  >
                    {message.content}
                    {/*
                      A imagem anexada era enviada para a IA e descartada: o
                      balao mostrava apenas a legenda "Imagem anexada", nunca a
                      imagem. Agora ela aparece de verdade -- da previa local
                      enquanto a conversa esta aberta, e do endpoint autenticado
                      quando ela e reaberta pelo historico.
                    */}
                    {(message.imagePreview || message.imagePath || message.hasImage) && (
                      <OrbyImage
                        preview={message.imagePreview}
                        path={message.imagePath}
                        onAmpliar={setImagemAmpliada}
                      />
                    )}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    fontSize: '0.7rem',
                    // Horário: no balão do usuário (fundo azul) usa contrastText;
                    // no do assistente, o secundário do tema (item 19).
                    color: message.role === 'user' ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                    fontWeight: message.role === 'user' ? 500 : 400,
                    opacity: message.role === 'user' ? 0.8 : 0.7,
                  }}
                >
                  {message.timestamp.toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
                {message.role === 'assistant' && !message.error && message.content && !message.isWelcome && (
                  <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25, ml: -0.5 }}>
                    <Tooltip title="Copiar resposta" arrow>
                      <IconButton size="small" onClick={() => copyOrbyMessage(message.content)} sx={{ p: 0.4, color: theme.palette.text.secondary }}>
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Inserir no ticket/chat aberto" arrow>
                      <IconButton size="small" onClick={() => insertOrbyMessage(message.content)} sx={{ p: 0.4, color: theme.palette.text.secondary }}>
                        <InsertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: '1px', height: 16, bgcolor: theme.palette.divider, mx: 0.5, alignSelf: 'center' }} />
                    {(() => {
                      const fb = feedbackByResponse[message.content];
                      const liked = fb === 'like';
                      const disliked = fb === 'dislike';
                      // A dica diz o que o clique FAZ -- inclusive "clique para
                      // desmarcar" e o motivo quando nao da. Antes o botao
                      // marcado nao reagia a nada, em silencio.
                      const atual = fb ? { rating: fb, status: feedbackMetaByResponse[message.content]?.status ?? null } : null;
                      return (
                        <>
                          <Tooltip title={dicaDoPolegar(atual, 'like')} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleLike(messages[index - 1]?.content || '', message.content)}
                              sx={{
                                p: 0.4,
                                borderRadius: 1.5,
                                color: liked ? theme.palette.success.main : theme.palette.text.secondary,
                                bgcolor: liked ? 'rgba(34,197,94,0.16)' : 'transparent',
                                opacity: disliked ? 0.35 : 1,
                                '&:hover': { bgcolor: liked ? 'rgba(34,197,94,0.24)' : theme.palette.action.hover },
                              }}
                            >
                              {liked ? <ThumbUpFilledIcon sx={{ fontSize: 15 }} /> : <ThumbUpIcon sx={{ fontSize: 15 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={dicaDoPolegar(atual, 'dislike')} arrow>
                            <IconButton
                              size="small"
                              onClick={() => openDislike(messages[index - 1]?.content || '', message.content)}
                              sx={{
                                p: 0.4,
                                borderRadius: 1.5,
                                color: disliked ? theme.palette.error.main : theme.palette.text.secondary,
                                bgcolor: disliked ? 'rgba(239,68,68,0.16)' : 'transparent',
                                opacity: liked ? 0.35 : 1,
                                '&:hover': { bgcolor: disliked ? 'rgba(239,68,68,0.24)' : theme.palette.action.hover },
                              }}
                            >
                              {disliked ? <ThumbDownFilledIcon sx={{ fontSize: 15 }} /> : <ThumbDownIcon sx={{ fontSize: 15 }} />}
                            </IconButton>
                          </Tooltip>
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Paper>

              {message.role === 'user' && (
                <Avatar
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    width: 32,
                    height: 32,
                  }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>
              )}
            </Box>
          ))}
          
          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 32,
                  height: 32,
                }}
              >
                <Box
                  component="img"
                  src={theme.palette.mode === 'dark' ? discoveryIconWhite : discoveryIconBlack}
                  alt=""
                  sx={{ width: 20, height: 20, objectFit: 'contain' }}
                />
              </Avatar>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
                  border: theme.palette.mode === 'light' ? '1px solid #e0e0e0' : 'none',
                }}
              >
                <CircularProgress size={16} />
              </Paper>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        <Divider />
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 0.75,
        }}
      >
        {attachedImage && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box
              component="img"
              src={attachedImagePreview}
              alt="Anexo"
              sx={{
                width: 48,
                height: 48,
                objectFit: 'cover',
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {attachedImage.name}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setAttachedImage(null);
                setAttachedImagePreview(null);
              }}
              sx={{ color: theme.palette.error.main }}
              title="Remover imagem"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {(attachedContext || openContextKind) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {attachedContext ? (
              <Tooltip title="O Orby vai considerar este conteúdo na próxima pergunta" arrow>
                <Chip
                  size="small"
                  icon={<ContextIcon sx={{ fontSize: 15 }} />}
                  label={`Contexto: ${attachedContext.label}`}
                  onDelete={() => setAttachedContext(null)}
                  sx={{ maxWidth: '100%' }}
                />
              </Tooltip>
            ) : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContextIcon sx={{ fontSize: 16 }} />}
                onClick={requestOpenContext}
                sx={{ textTransform: 'none', py: 0.25 }}
              >
                {openContextKind === 'ticket' ? 'Usar este ticket' : 'Usar este chat'}
              </Button>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Tooltip title="Anexar imagem (máx. 5MB)" placement="top" arrow>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || historyOpen}
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                color: theme.palette.text.secondary,
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder={attachedImage ? "Descreva a imagem ou faça uma pergunta (opcional)..." : "Digite sua dúvida ou cole uma imagem (Ctrl+V)..."}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onKeyDown={(e) => {
              const isShortcut = e.ctrlKey && e.shiftKey && String(e.key || '').toLowerCase() === 'g';
              if (isShortcut) {
                e.preventDefault();
                handleAdjustInputWithGpt();
              }
            }}
            onPaste={handlePaste}
            disabled={loading || historyOpen}
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                minHeight: 40,
                alignItems: 'center',
                backgroundColor: theme.palette.background.textField,
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.text.secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
                py: 1,
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={gptAdjustingInputMessage ? 'Orby está digitando...' : 'Ajustar texto (Orby) — Ctrl+Shift+G'}
                    placement="top"
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleAdjustInputWithGpt}
                        disabled={loading || historyOpen || gptAdjustingInputMessage}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: 0.75,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        aria-label={gptAdjustingInputMessage ? 'Orby está digitando' : 'Ajustar texto com Orby'}
                      >
                        <OrbyInlineIcon loading={gptAdjustingInputMessage} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={(!inputMessage.trim() && !attachedImage) || loading || historyOpen}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              '&:disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
          </IconButton>
          </Box>
          {gptAdjustingInputMessage && (
            <Typography
              variant="caption"
              component="div"
              sx={{
                display: 'block',
                color: theme.palette.text.secondary,
                pl: 6,
                lineHeight: 1.2,
              }}
            >
              Orby está digitando…
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.25 }}>
            <Button
              size="small"
              onClick={handleClearChat}
              sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 28, py: 0.25 }}
            >
              Nova conversa
            </Button>
          </Box>
        </Box>
      </DialogActions>

      {/* Justificativa do dislike (vai para a fila de análise) */}
      <Dialog open={!!dislikeDialog} onClose={() => setDislikeDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>O que ficou errado nesta resposta?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Sua justificativa vai para análise de quem cuida da Base de Conhecimento e ajuda a melhorar as respostas do Orby.
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            placeholder="Ex.: a resposta não considerou o cenário X, ou a informação está desatualizada..."
            value={dislikeText}
            onChange={(e) => setDislikeText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDislikeDialog(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={submitDislike} disabled={!dislikeText.trim()} sx={{ textTransform: 'none' }}>
            Enviar para análise
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer de histórico */}
      <Drawer
        anchor="right"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        ModalProps={{
          keepMounted: false,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
        }}
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: '80vw',
            zIndex: 1400, // Acima do Dialog (1300)
          }
        }}
        sx={{
          zIndex: 1400,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon />
              Histórico
            </Typography>
            <IconButton
              onClick={() => setHistoryOpen(false)}
              size="small"
              sx={{ ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {loadingConversations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : conversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              Nenhuma conversa salva ainda
            </Typography>
          ) : (
            <List>
              {conversations.map((conv) => (
                <ListItem
                  key={conv.id}
                  button
                  onClick={() => loadConversation(conv.conversation_id)}
                  selected={conversationId === conv.conversation_id}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.light,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.light,
                      }
                    }
                  }}
                >
                  <ListItemText
                    primary={conv.title || 'Sem título'}
                    secondary={new Date(conv.updated_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '0.9rem',
                        fontWeight: conversationId === conv.conversation_id ? 600 : 400
                      }
                    }}
                    secondaryTypographyProps={{
                      sx: { fontSize: '0.75rem' }
                    }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => deleteConversation(conv.conversation_id, e)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/*
        Ampliar a imagem anexada. O balao e estreito -- uma captura de tela,
        que e o caso comum aqui, fica ilegivel sem isto.
      */}
      <Dialog
        open={!!imagemAmpliada}
        onClose={() => setImagemAmpliada(null)}
        maxWidth="lg"
      >
        <Box
          component="img"
          src={imagemAmpliada || undefined}
          alt="Imagem anexada à pergunta"
          onClick={() => setImagemAmpliada(null)}
          sx={{
            display: 'block',
            maxWidth: '90vw',
            maxHeight: '85vh',
            cursor: 'zoom-out',
          }}
        />
      </Dialog>
    </>
  );

  // Modo painel (encaixado no layout, lado a lado com o conteúdo): renderiza o mesmo
  // conteúdo sem o wrapper de modal, preenchendo o container pai.
  if (variant === 'panel') {
    return (
      <Box
        className="orby-panel"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {body}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      disableEnforceFocus={historyOpen || !!dislikeDialog}
      disableAutoFocus={historyOpen}
      PaperProps={{
        sx: fullScreen
          ? {
              height: '100vh',
              maxHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1300,
            }
          : {
              height: '80vh',
              maxHeight: '700px',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1300,
            }
      }}
    >
      {body}
    </Dialog>
  );
};

export default AIAssistant;
